import * as RadixHoverCard from '@radix-ui/react-hover-card';
import type { ReactNode } from 'react';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

type HoverCardProps = {
  content: ReactNode;
  children: ReactNode;
  /**
   * Preferred side. Defaults to `top` because in a dense table no side is
   * "free", and `top` is the least disruptive: it covers prior rows (already
   * scanned past) while leaving the trigger row + the columns to its left
   * and right intact. Narrow indicator-style cards that fit beside a thin
   * column should override to `right`. Radix collision detection flips to
   * the opposite side automatically near viewport edges.
   */
  side?: Side;
  align?: Align;
  /** ms before the card opens on hover. Default 200ms — fast enough to feel responsive on a deliberate hover, slow enough to ignore mouse fly-bys. */
  openDelay?: number;
  /** ms before the card closes after the mouse leaves. The grace window lets users move into the card without dismissing it. */
  closeDelay?: number;
  /** Render the trigger inline (no wrapper element). The child must accept ref + standard event handlers. */
  asChild?: boolean;
};

export function HoverCard({
  content,
  children,
  side = 'top',
  align = 'center',
  openDelay = 200,
  closeDelay = 150,
  asChild = true,
}: HoverCardProps) {
  if (content === null || content === undefined) {
    return <>{children}</>;
  }

  return (
    <RadixHoverCard.Root openDelay={openDelay} closeDelay={closeDelay}>
      <RadixHoverCard.Trigger asChild={asChild}>{children}</RadixHoverCard.Trigger>
      <RadixHoverCard.Portal>
        <RadixHoverCard.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          avoidCollisions
          className="z-popover max-w-sm rounded-md bg-popover p-4 text-sm text-popover-foreground shadow-popover data-[state=open]:animate-[popover-fade-in_180ms_ease-out]"
        >
          {content}
        </RadixHoverCard.Content>
      </RadixHoverCard.Portal>
    </RadixHoverCard.Root>
  );
}
